import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import API_BASE, { listGearSets, withApiCredentials } from "../../../api";
import { getActiveAwareAuthToken } from "../../../auth/bootstrapTokens";
import { extractExifData } from "../../../utils/exifUtils";
import {
  buildFishSpeciesSuggestions,
  getTypeOfFishingOptionsForWay,
  isLureFishingType,
  WAY_OF_FISHING_OPTIONS,
} from "../../../utils/catchMetadataOptions";
import {
  getBaitSuggestions,
  getLureSuggestions,
  getReelSuggestions,
  getRodSuggestions,
} from "../../CreatePostModal/catch-form/catchFormGearHelpers";
import {
  appendCurrentSelectOption,
  buildPastTripCatchInitialState,
  buildPastTripCatchPayload,
  buildPastTripStyleOptions,
  buildPastTripTechniqueOptions,
  dedupeStringValues,
  formatPastTripCoordinates,
  formatTripDateRangeCopy,
  inferWayOfFishingFromType,
  normalizePastTripCoordinatesInput,
  validatePastTripCatchSubmission,
} from "./PastTripCatchValidation";

const getStringValue = (value) =>
  typeof value === "string" ? value : value == null ? "" : String(value);

const normalizeSavedGearSet = (rawGearSet) => ({
  id: getStringValue(rawGearSet?.id),
  name: getStringValue(rawGearSet?.name),
  rod: getStringValue(rawGearSet?.rod),
  reel: getStringValue(rawGearSet?.reel),
  lure: getStringValue(rawGearSet?.lure),
});

export default function usePastTripCatchState({
  trip,
  userId,
  initialFormState,
  mapPickedCoordinates,
  mapPickSelectionId,
  onPickCoordinatesFromMap,
  onClose,
  onSaved,
}) {
  const { t } = useTranslation();
  const [formState, setFormState] = useState(() =>
    ({
      ...buildPastTripCatchInitialState(trip),
      ...(initialFormState && typeof initialFormState === "object"
        ? initialFormState
        : {}),
    })
  );
  const [mediaFile, setMediaFile] = useState(null);
  const [error, setError] = useState("");
  const [coordinateError, setCoordinateError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [mediaMetadataMessage, setMediaMetadataMessage] = useState("");
  const [fishList, setFishList] = useState([]);
  const [fishingMethods, setFishingMethods] = useState({});
  const [rodDatabase, setRodDatabase] = useState([]);
  const [reelDatabase, setReelDatabase] = useState([]);
  const [lureDatabase, setLureDatabase] = useState([]);
  const [baitDatabase, setBaitDatabase] = useState([]);
  const [liveBaitDatabase, setLiveBaitDatabase] = useState([]);
  const [deadBaitDatabase, setDeadBaitDatabase] = useState([]);
  const [savedGearSets, setSavedGearSets] = useState([]);
  const [isLoadingSavedGearSets, setIsLoadingSavedGearSets] = useState(false);
  const [savedGearSetLoadError, setSavedGearSetLoadError] = useState("");
  const appliedMapPickRef = useRef("");

  const tripDateHint = formatTripDateRangeCopy(trip?.startDate, trip?.endDate);
  const minDate = trip?.startDate || "";
  const maxDate = trip?.endDate || trip?.startDate || "";
  const modalFieldIdBase = `past-trip-catch-${trip?.id || "new"}`;

  useEffect(() => {
    let cancelled = false;

    async function loadCatchOptions() {
      try {
        const [
          nextFishList,
          nextFishingMethods,
          nextRodDatabase,
          nextReelDatabase,
          nextLureDatabase,
          nextBaitDatabase,
          nextLiveBaitDatabase,
          nextDeadBaitDatabase,
        ] = await Promise.all([
          fetch("/JSON/fish-data.json").then((res) => res.json()),
          fetch(`${process.env.PUBLIC_URL}/JSON/fishingMethods.json`).then((res) =>
            res.json()
          ),
          fetch("/JSON/rod-data.json").then((res) => res.json()),
          fetch("/JSON/reel-data.json").then((res) => res.json()),
          fetch("/JSON/lure-data.json").then((res) => res.json()),
          fetch("/JSON/bait-data.json").then((res) => res.json()),
          fetch("/JSON/live-bait-data.json").then((res) => res.json()),
          fetch("/JSON/dead-bait-data.json").then((res) => res.json()),
        ]);

        if (cancelled) return;

        setFishList(Array.isArray(nextFishList) ? nextFishList : []);
        setFishingMethods(
          nextFishingMethods && typeof nextFishingMethods === "object"
            ? nextFishingMethods
            : {}
        );
        setRodDatabase(Array.isArray(nextRodDatabase) ? nextRodDatabase : []);
        setReelDatabase(Array.isArray(nextReelDatabase) ? nextReelDatabase : []);
        setLureDatabase(Array.isArray(nextLureDatabase) ? nextLureDatabase : []);
        setBaitDatabase(Array.isArray(nextBaitDatabase) ? nextBaitDatabase : []);
        setLiveBaitDatabase(
          Array.isArray(nextLiveBaitDatabase) ? nextLiveBaitDatabase : []
        );
        setDeadBaitDatabase(
          Array.isArray(nextDeadBaitDatabase) ? nextDeadBaitDatabase : []
        );
      } catch (loadError) {
        console.error("Failed to load Past Trip catch options:", loadError);
      }
    }

    loadCatchOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!userId) {
      setSavedGearSets([]);
      setSavedGearSetLoadError("");
      setIsLoadingSavedGearSets(false);
      return undefined;
    }

    const token =
      typeof window !== "undefined" ? getActiveAwareAuthToken(window) : "";
    const controller = new AbortController();

    setIsLoadingSavedGearSets(true);
    setSavedGearSetLoadError("");

    listGearSets(token || "", { signal: controller.signal })
      .then((gearSets) => {
        if (controller.signal.aborted) return;
        setSavedGearSets(
          Array.isArray(gearSets) ? gearSets.map(normalizeSavedGearSet) : []
        );
      })
      .catch((loadError) => {
        if (loadError?.name === "AbortError") return;
        setSavedGearSets([]);
        setSavedGearSetLoadError("catch_saved_gear_load_error");
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoadingSavedGearSets(false);
        }
      });

    return () => controller.abort();
  }, [userId]);

  useEffect(() => {
    if (formState.wayOfFishing || !formState.typeOfFishing) return;
    if (!fishingMethods || Object.keys(fishingMethods).length === 0) return;

    const inferredWay = inferWayOfFishingFromType(
      fishingMethods,
      formState.typeOfFishing
    );

    if (!inferredWay) return;

    setFormState((prev) => ({
      ...prev,
      wayOfFishing: inferredWay,
    }));
  }, [fishingMethods, formState.typeOfFishing, formState.wayOfFishing]);

  useEffect(() => {
    if (!mapPickedCoordinates) return;

    const selectionKey = String(
      mapPickSelectionId || mapPickedCoordinates || ""
    );
    if (!selectionKey || appliedMapPickRef.current === selectionKey) return;

    const normalized = normalizePastTripCoordinatesInput(mapPickedCoordinates);
    if (!normalized.isValid || !normalized.value) return;

    appliedMapPickRef.current = selectionKey;
    setCoordinateError("");
    setFormState((prev) => ({
      ...prev,
      coordinates: normalized.value,
    }));
  }, [mapPickedCoordinates, mapPickSelectionId]);

  const fishSuggestions = dedupeStringValues(
    buildFishSpeciesSuggestions(fishList, formState.fish).map(
      (fish) => fish?.commonName || ""
    )
  );
  const typeOfFishingOptions = appendCurrentSelectOption(
    getTypeOfFishingOptionsForWay(fishingMethods, formState.wayOfFishing),
    formState.typeOfFishing
  );
  const styleOptions = appendCurrentSelectOption(
    buildPastTripStyleOptions(
      fishingMethods,
      formState.wayOfFishing,
      formState.typeOfFishing
    ),
    formState.style
  );
  const techniqueOptions = appendCurrentSelectOption(
    buildPastTripTechniqueOptions(
      fishingMethods,
      formState.wayOfFishing,
      formState.typeOfFishing,
      formState.style
    ),
    formState.technique
  );
  const showLureField = isLureFishingType(formState.typeOfFishing);
  const rodSuggestions = getRodSuggestions(rodDatabase, formState.rod).map(
    (suggestion) => suggestion.value
  );
  const reelSuggestions = getReelSuggestions(reelDatabase, formState.reel).map(
    (suggestion) => suggestion.value
  );
  const lureSuggestions = getLureSuggestions(lureDatabase, formState.lure).map(
    (suggestion) => suggestion.value
  );
  const baitSuggestions = getBaitSuggestions(formState.bait, {
    baitDatabase,
    liveBaitDatabase,
    deadBaitDatabase,
  });
  const shouldShowSavedGearSelector =
    isLoadingSavedGearSets ||
    Boolean(savedGearSetLoadError) ||
    savedGearSets.length > 0;

  const clearError = useCallback(() => {
    if (error) {
      setError("");
    }
  }, [error]);

  const clearCoordinateError = useCallback(() => {
    if (coordinateError) {
      setCoordinateError("");
    }
  }, [coordinateError]);

  const handleWayChange = (event) => {
    const nextWay = event.target.value;
    setFormState((prev) => ({
      ...prev,
      wayOfFishing: nextWay,
      typeOfFishing: "",
      style: "",
      technique: "",
      savedGearSetId: "",
    }));
    clearError();
  };

  const handleTypeChange = (event) => {
    const nextType = event.target.value;
    setFormState((prev) => ({
      ...prev,
      typeOfFishing: nextType,
      style: "",
      technique: "",
      savedGearSetId: "",
    }));
    clearError();
  };

  const handleStyleChange = (event) => {
    const nextStyle = event.target.value;
    setFormState((prev) => ({
      ...prev,
      style: nextStyle,
      technique: "",
    }));
    clearError();
  };

  const handleFieldChange = (field) => (event) => {
    const nextValue = event.target.value;
    setFormState((prev) => ({
      ...prev,
      [field]: nextValue,
    }));
    if (field === "coordinates") {
      clearCoordinateError();
    }
    clearError();
  };

  const handleCoordinatesBlur = useCallback(() => {
    const normalized = normalizePastTripCoordinatesInput(formState.coordinates);
    if (!normalized.value) {
      setCoordinateError("");
      return;
    }

    if (!normalized.isValid) {
      setCoordinateError("trip_add_catch_coordinates_invalid");
      return;
    }

    setCoordinateError("");
    setFormState((prev) => ({
      ...prev,
      coordinates: normalized.value,
    }));
  }, [formState.coordinates]);

  const handlePickCoordinatesFromMap = useCallback(() => {
    onPickCoordinatesFromMap?.({ ...formState });
  }, [formState, onPickCoordinatesFromMap]);

  const applySavedGearSet = useCallback(
    (gearSet) => {
      const savedRod = getStringValue(gearSet?.rod).trim();
      const savedReel = getStringValue(gearSet?.reel).trim();
      const savedLure = getStringValue(gearSet?.lure).trim();

      setFormState((prev) => ({
        ...prev,
        rod: savedRod || prev.rod,
        reel: savedReel || prev.reel,
        lure: showLureField && savedLure ? savedLure : prev.lure,
        bait: !showLureField && savedLure ? savedLure : prev.bait,
      }));
    },
    [showLureField]
  );

  const handleSavedGearSetChange = useCallback(
    (event) => {
      const nextGearSetId = event.target.value;
      if (!nextGearSetId) {
        setFormState((prev) => ({
          ...prev,
          savedGearSetId: "",
        }));
        return;
      }

      const selectedGearSet = savedGearSets.find(
        (gearSet) => gearSet.id === nextGearSetId
      );
      if (!selectedGearSet) return;

      const currentLureOrBait = showLureField ? formState.lure : formState.bait;
      const hasCurrentGearFields = [
        formState.rod,
        formState.reel,
        currentLureOrBait,
      ].some((value) => getStringValue(value).trim());
      const shouldApply =
        !hasCurrentGearFields ||
        typeof window === "undefined" ||
        window.confirm(t("catch_saved_gear_apply_confirm"));

      if (!shouldApply) return;

      applySavedGearSet(selectedGearSet);
      setFormState((prev) => ({
        ...prev,
        savedGearSetId: nextGearSetId,
      }));
      clearError();
    },
    [
      applySavedGearSet,
      formState.bait,
      formState.lure,
      formState.reel,
      formState.rod,
      savedGearSets,
      showLureField,
      clearError,
      t,
    ]
  );

  const handleRequestClose = () => {
    if (isSaving) return;
    onClose?.();
  };

  const applyExifToPastTripCatch = async (file) => {
    if (!file) {
      setMediaMetadataMessage("");
      return;
    }

    try {
      const exif = await extractExifData(file);
      if (!exif) {
        setMediaMetadataMessage("");
        return;
      }

      let detectedDate = "";
      let detectedTime = "";
      let detectedCoordinates = "";

      if (
        typeof exif.datetimeOriginal === "string" &&
        exif.datetimeOriginal.includes("T")
      ) {
        const [datePart, timePart] = exif.datetimeOriginal.split("T");

        if (datePart) {
          detectedDate = datePart;
        }

        if (timePart) {
          detectedTime = timePart.slice(0, 5);
        }
      }

      if (exif.gps) {
        detectedCoordinates = formatPastTripCoordinates(exif.gps.lat, exif.gps.lng);
      }

      setFormState((prev) => ({
        ...prev,
        date: detectedDate || prev.date,
        time: detectedTime || prev.time,
        coordinates: detectedCoordinates || prev.coordinates,
      }));

      const detectedParts = [];
      if (detectedDate || detectedTime) {
        detectedParts.push(
          t("trip_add_catch_photo_datetime_detected", {
            defaultValue: "Date and time were detected from the photo.",
          })
        );
      }
      if (detectedCoordinates) {
        detectedParts.push(
          t("trip_add_catch_photo_coordinates_detected", {
            defaultValue: "Coordinates were detected from the photo.",
          })
        );
      }

      setMediaMetadataMessage(detectedParts.join(" "));
    } catch (exifError) {
      console.error("Failed to extract EXIF data for Past Trip catch:", exifError);
      setMediaMetadataMessage("");
    }
  };

  const handleMediaChange = async (event) => {
    const nextFile = event.target.files?.[0] || null;
    setMediaFile(nextFile);
    await applyExifToPastTripCatch(nextFile);
    clearError();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSaving) return;

    const token =
      typeof window !== "undefined" ? window.localStorage.getItem("token") : "";
    if (!trip?.id || !userId) {
      setError(
        t("trip_error_session_expired", {
          defaultValue: "Your session expired. Please sign in again.",
        })
      );
      return;
    }

    const validationError = validatePastTripCatchSubmission({
      formState,
      minDate,
      maxDate,
      t,
    });

    if (validationError) {
      setError(validationError);
      if (!normalizePastTripCoordinatesInput(formState.coordinates).isValid) {
        setCoordinateError("trip_add_catch_coordinates_invalid");
      }
      return;
    }

    const normalizedCoordinates = normalizePastTripCoordinatesInput(
      formState.coordinates
    );
    const payloadFormState = {
      ...formState,
      coordinates: normalizedCoordinates.value,
    };
    setCoordinateError("");
    if (payloadFormState.coordinates !== formState.coordinates) {
      setFormState(payloadFormState);
    }

    const catchPayload = buildPastTripCatchPayload({
      formState: payloadFormState,
      showLureField,
    });
    const formData = new FormData();
    formData.append("catchData", JSON.stringify(catchPayload));
    if (token) {
      formData.append("token", token);
    }
    if (mediaFile) {
      formData.append("media", mediaFile);
    }

    setIsSaving(true);
    setError("");

    try {
      const res = await fetch(
        `${API_BASE}/trips/${trip.id}/catches?userId=${encodeURIComponent(
          userId
        )}`,
        withApiCredentials({
          method: "POST",
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : undefined,
          body: formData,
        })
      );

      if (res.status === 401) {
        setError(
          t("trip_error_session_expired", {
            defaultValue: "Your session expired. Please sign in again.",
          })
        );
        return;
      }

      if (!res.ok) {
        let detail = "";
        try {
          const payload = await res.json();
          detail = typeof payload?.detail === "string" ? payload.detail : "";
        } catch {
          detail = "";
        }

        throw new Error(
          detail ||
            t("trip_add_catch_save_error", {
              defaultValue: "Could not save this catch to the trip.",
            })
        );
      }

      const updatedTrip = await res.json();
      await onSaved?.(updatedTrip);
    } catch (err) {
      setError(
        err?.message ||
          t("trip_add_catch_save_error", {
            defaultValue: "Could not save this catch to the trip.",
          })
      );
    } finally {
      setIsSaving(false);
    }
  };

  return {
    formState,
    mediaFile,
    error,
    coordinateError,
    isSaving,
    mediaMetadataMessage,
    tripDateHint,
    minDate,
    maxDate,
    modalFieldIdBase,
    fishSuggestions,
    wayOfFishingOptions: WAY_OF_FISHING_OPTIONS,
    typeOfFishingOptions,
    styleOptions,
    techniqueOptions,
    rodSuggestions,
    reelSuggestions,
    lureSuggestions,
    baitSuggestions,
    showLureField,
    savedGearSets,
    isLoadingSavedGearSets,
    savedGearSetLoadError,
    shouldShowSavedGearSelector,
    handleWayChange,
    handleTypeChange,
    handleStyleChange,
    handleSavedGearSetChange,
    handleFieldChange,
    handleCoordinatesBlur,
    handlePickCoordinatesFromMap,
    handleMediaChange,
    handleRequestClose,
    handleSubmit,
  };
}
